import { TableSort, SortHeading, type TableSortValue } from '../components/TableSort';
import { SANDBOX_LABEL, planSourceLabel } from '../utils/creditSource';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiCheckCircle, FiShield, FiFilter, FiTool, FiList, FiGrid, FiDownload } from 'react-icons/fi';
import { BsGem } from 'react-icons/bs';
import { apiDownload, apiRequest } from '../utils/auth';
import { formatDiamonds } from '../utils/diamondFormat';
import UserDetailsModal from '../components/UserDetailsModal';
import SubscriberSummary from '../components/SubscriberSummary';
import Pagination from '../components/Pagination';
import TableFilters, { type TableFilterField } from '../components/TableFilters/TableFilters';

interface User {
  _id: string;
  userId: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  preferredName?: string;
  profilePicture?: string;
  originalProfilePicture?: string;
  plan?: string;
  pendingPlan?: string;
  planSource?: string;
  planAcquisitionSource?: string;
  planPromoCode?: string;
  planEnvironment?: 'sandbox' | 'production';
  planGrantedBy?: string;
  planGrantedReason?: string;
  planChangedAt?: string;
  subscribePlatform?: 'AppleAppStore' | 'GooglePlayStore' | 'WebStore';
  planCredits: number;
  topUpCredits: number;
  freeCredits: number;
  totalCredits: number;
  diamonds: number;
  pendingDiamonds: number;
  expiredDiamonds: number;
  unlockedExtensionsCount: number;
  lastBillingDate?: string;
  nextBillingDate?: string;
  followingCount: number;
  followersCount: number;
  likedExtensionsCount: number;
  extensionsCreatedCount: number;
  bookmarkedExtensionsCount: number;
  isCreator?: boolean;
  isCreatorDisabled?: boolean;
  isAdmin?: boolean;
  isInternal?: boolean;
  canSetExtensionPublic?: boolean;
  referralCode?: string | null;
  referralCount?: number;
  maxReferrals?: number | null;
  effectiveMaxReferrals?: number;
  defaultMaxReferrals?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Filters {
  isCreator: string;
  isCreatorDisabled: string;
  subscribePlatform: string;
  planSource: string;
  plans: string[];               // multi-select: 'free' | 'basic' | 'pro' | 'premium' (empty = all)
  isAdmin: string;               // 'all' | 'true' | 'false'
  isInternal: string;            // 'all' | 'true' | 'false'
  canSetExtensionPublic: string; // 'all' | 'true' | 'false'
}

// Plans default to all selected — the panel opens reflecting the
// "showing everyone" state. A proper subset actually filters.
const ALL_PLANS = ['free', 'basic', 'pro', 'premium'];

const defaultFilters: Filters = {
  isCreator: 'all',
  isCreatorDisabled: 'all',
  subscribePlatform: 'all',
  planSource: 'all',
  plans: [...ALL_PLANS],
  isAdmin: 'all',
  isInternal: 'all',
  canSetExtensionPublic: 'all',
};

function countActiveFilters(f: Filters): number {
  let count = ['isCreator', 'isCreatorDisabled', 'subscribePlatform', 'planSource'].filter(key => f[key as keyof Filters] !== 'all').length;
  if (f.plans.length < ALL_PLANS.length) count++; // a proper subset is a real filter
  if (f.isAdmin !== 'all') count++;
  if (f.isInternal !== 'all') count++;
  if (f.canSetExtensionPublic !== 'all') count++;
  return count;
}

function buildFilterParams(f: Filters): Record<string, string> {
  const params: Record<string, string> = {};
  // All plans selected = no plan filter; only send when it's a proper subset.
  if (f.plans.length > 0 && f.plans.length < ALL_PLANS.length) params.plans = f.plans.join(',');
  if (f.isAdmin !== 'all') params.isAdmin = f.isAdmin;
  if (f.isInternal !== 'all') params.isInternal = f.isInternal;
  if (f.canSetExtensionPublic !== 'all') params.canSetExtensionPublic = f.canSetExtensionPublic;
  for (const key of ['isCreator', 'isCreatorDisabled', 'subscribePlatform', 'planSource'] as const) {
    if (f[key] !== 'all') params[key] = f[key];
  }
  return params;
}

function formatDate(dateString?: string) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
}

function planMeta(plan?: string): { label: string; color: string } {
  if (plan === 'basic') return { label: 'Basic', color: 'var(--color-accent-strong)' };
  if (plan === 'pro') return { label: 'Pro', color: 'var(--color-success)' };
  if (plan === 'premium') return { label: 'Premium', color: '#8957e5' };
  return { label: 'Free', color: 'var(--color-subtle)' };
}

// Card border accent flagging the subscription tier. Paid plans get a bolder
// coloured border; Free keeps the neutral 1px card border.
function tierAccent(plan?: string): { color: string; width: number } {
  if (plan === 'basic') return { color: 'var(--color-accent-strong)', width: 2 };
  if (plan === 'pro') return { color: 'var(--color-success)', width: 2 };
  if (plan === 'premium') return { color: '#8957e5', width: 2 };
  return { color: 'var(--color-border)', width: 1 };
}

function Avatar({ user, size = 36 }: { user: User; size?: number }) {
  if (user.profilePicture) {
    return <img src={user.profilePicture} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block', flexShrink: 0 }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: Math.round(size * 0.4), fontWeight: 600, flexShrink: 0 }}>
      {(user.preferredName || user.name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function AdminBadge() {
  return (
    <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: '#3a2a17', color: 'var(--color-warning)', border: '1px solid var(--color-warning)', fontSize: '11px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <FiShield size={12} /> Admin
    </span>
  );
}

function InternalBadge() {
  return (
    <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: '#1c2b3a', color: 'var(--color-accent)', border: '1px solid var(--color-accent)', fontSize: '11px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <FiTool size={12} /> Internal
    </span>
  );
}

function PlanBadge({ user }: { user: User }) {
  const { label, color } = planMeta(user.plan);
  const sourceLabel = planSourceLabel(user.plan, user.planSource, user.planAcquisitionSource);
  const sourceColor = sourceLabel === 'Admin grant' ? '#8957e5' : sourceLabel === 'Promo code' || sourceLabel === 'Promotional grant' ? '#9a6700' : '#57606a';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: color, color: 'white', fontSize: '12px', fontWeight: 500 }}>{label}</span>
      {user.plan && user.plan !== 'free' && sourceLabel !== 'Self-subscribed' && (
        <span title="Subscription source" style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: sourceColor, color: 'white', fontSize: '12px', fontWeight: 500 }}>{sourceLabel}</span>
      )}
      {user.planEnvironment === 'sandbox' && (
        <span title="Test subscription" style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: '#9a6700', color: 'white', fontSize: '12px', fontWeight: 500 }}>{SANDBOX_LABEL}</span>
      )}
      {user.pendingPlan && <span style={{ fontSize: '12px', color: 'var(--color-warning-strong)' }}>→ {planMeta(user.pendingPlan).label}</span>}
    </span>
  );
}

function DiamondCount({ count }: { count: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: count > 0 ? 'var(--color-accent)' : 'var(--color-subtle)', fontWeight: 600, whiteSpace: 'nowrap' }}>
      <BsGem size={12} /> {formatDiamonds(count)}
    </span>
  );
}

function PublicRightStatus({ user }: { user: User }) {
  if (!user.canSetExtensionPublic) {
    return null;
  }
  return (
    <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-success)', display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
      <FiCheckCircle size={14} /> Public
    </span>
  );
}

type ViewMode = 'list' | 'grid';

const GRID_PAGE_SIZE = 30;

function UserGridCard({ user, onClick }: { user: User; onClick: () => void }) {
  const accent = tierAccent(user.plan);
  return (
    <div
      onClick={onClick}
      style={{ backgroundColor: 'var(--color-surface)', border: `${accent.width}px solid ${accent.color}`, boxSizing: 'border-box', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', cursor: 'pointer', transition: 'border-color 0.15s' }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = accent.color)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <Avatar user={user} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{user.preferredName || user.name || '-'}</span>
            {user.isAdmin && <AdminBadge />}
            {user.isInternal && <InternalBadge />}
          </div>
          {user.preferred_username && <div style={{ fontSize: '12px', color: 'var(--color-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{user.preferred_username.replace(/^@+/, '')}</div>}
          {user.email && (
            <div style={{ fontSize: '12px', color: 'var(--color-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px' }}>
        <PlanBadge user={user} />
      </div>
      <dl aria-label="User statistics" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px 8px', margin: 0, padding: '12px 0', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
        {[
          { label: 'Credits', value: (user.totalCredits ?? 0).toLocaleString() },
          { label: 'Followers', value: (user.followersCount ?? 0).toLocaleString() },
          { label: 'Following', value: (user.followingCount ?? 0).toLocaleString() },
          { label: 'Liked', value: (user.likedExtensionsCount ?? 0).toLocaleString() },
          { label: 'Lenses', value: (user.extensionsCreatedCount ?? 0).toLocaleString() },
          { label: 'Diamonds', value: <DiamondCount count={user.diamonds} /> },
        ].map(({ label, value }) => (
          <div key={label} style={{ minWidth: 0 }}>
            <dt style={{ fontSize: '11px', color: 'var(--color-muted)', marginBottom: '4px' }}>{label}</dt>
            <dd style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--color-text)', overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}>{value}</dd>
          </div>
        ))}
      </dl>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', fontSize: '12px', color: 'var(--color-muted)' }}>
        <PublicRightStatus user={user} />
        <span>{formatDate(user.createdAt)}</span>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const navigate = useNavigate();
  // When the URL is /users/:userId the detail modal is opened for that user
  // (supports deep-linking / refresh, not just in-app row clicks).
  const { userId: routeUserId } = useParams<{ userId: string }>();
  const [users, setUsers] = useState<User[]>([]);
  const [summaryVersion, setSummaryVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [sort, setSort] = useState<TableSortValue>({ field: 'createdAt', direction: 'desc' });
  const changeSort = (value: TableSortValue) => { setCurrentPage(1); setSort(value); };
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportInProgress = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  const [showFilters, setShowFilters] = useState(false);
  // Filters auto-apply: every change updates this and immediately re-fetches.
  const [appliedFilters, setAppliedFilters] = useState<Filters>(defaultFilters);

  // View mode (list = paginated table/cards, grid = infinite-scroll cards)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'grid';
    return (localStorage.getItem('usersViewMode') as ViewMode) || 'grid';
  });
  // Grid-view state: infinite scroll (respects the same search + filter panel)
  const [gridUsers, setGridUsers] = useState<User[]>([]);
  const [gridPage, setGridPage] = useState(1);
  const [gridTotal, setGridTotal] = useState(0);
  const [gridHasMore, setGridHasMore] = useState(false);
  const [gridInitialLoading, setGridInitialLoading] = useState(false);
  const [gridLoadingMore, setGridLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const listRequestId = useRef(0);
  const gridRequestId = useRef(0);

  // On mobile the list/grid toggle is hidden and we always show the grid.
  const effectiveViewMode: ViewMode = isMobile ? 'grid' : viewMode;

  const activeFilterCount = countActiveFilters(appliedFilters);

  useEffect(() => {
    localStorage.setItem('usersViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchUsers = async (page: number = 1, search: string = '', filters: Filters = appliedFilters, limit: number = pageSize) => {
    const requestId = ++listRequestId.current;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        sortBy: sort.field, sortOrder: sort.direction,
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...buildFilterParams(filters)
      });
      const response = await apiRequest<{
        success: boolean;
        data: User[];
        pagination: PaginationInfo;
        error?: string;
      }>(`/api/admin/users?${params}`);
      if (requestId !== listRequestId.current) return;
      if (response.success) {
        setUsers(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to fetch users');
      }
    } catch (err) {
      if (requestId !== listRequestId.current) return;
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      if (requestId === listRequestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (effectiveViewMode !== 'list') return;
    fetchUsers(currentPage, searchQuery, appliedFilters, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveViewMode, currentPage, searchQuery, appliedFilters, pageSize, sort]);

  // Grid view fetch: page-by-page, appending for infinite scroll. Respects the
  // same search + filter panel (plans, admin, internal, public-right) as the list.
  const fetchGridUsers = useCallback(async (page: number, replace: boolean) => {
    const requestId = ++gridRequestId.current;
    try {
      if (replace) { setGridInitialLoading(true); setGridLoadingMore(false); setGridHasMore(false); }
      else setGridLoadingMore(true);
      setError(null);
      const params = new URLSearchParams({
        sortBy: sort.field, sortOrder: sort.direction,
        page: page.toString(),
        limit: GRID_PAGE_SIZE.toString(),
        ...(searchQuery && { search: searchQuery }),
        ...buildFilterParams(appliedFilters),
      });
      const response = await apiRequest<{
        success: boolean;
        data: User[];
        pagination: PaginationInfo;
        error?: string;
      }>(`/api/admin/users?${params}`);
      if (requestId !== gridRequestId.current) return;
      if (response.success) {
        setGridUsers((prev) => (replace ? response.data : [...prev, ...response.data]));
        setGridPage(response.pagination.page);
        setGridTotal(response.pagination.total);
        setGridHasMore(response.pagination.page < response.pagination.pages);
      } else {
        setError(response.error || 'Failed to fetch users');
      }
    } catch (err) {
      if (requestId !== gridRequestId.current) return;
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      if (requestId === gridRequestId.current) { setGridInitialLoading(false); setGridLoadingMore(false); }
    }
  }, [appliedFilters, searchQuery, sort]);

  // Reset + load first page whenever the grid becomes active or its inputs change
  useEffect(() => {
    if (effectiveViewMode !== 'grid') return;
    fetchGridUsers(1, true);
  }, [effectiveViewMode, fetchGridUsers]);

  // Infinite scroll: load the next page when the sentinel scrolls into view
  useEffect(() => {
    if (effectiveViewMode !== 'grid') return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && gridHasMore && !gridLoadingMore && !gridInitialLoading) {
          fetchGridUsers(gridPage + 1, false);
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [effectiveViewMode, gridHasMore, gridLoadingMore, gridInitialLoading, gridPage, fetchGridUsers]);

  const handleExport = async () => {
    if (exportInProgress.current) return;
    exportInProgress.current = true;
    setExporting(true);
    setExportError(null);
    try {
      await apiDownload('/api/admin/users/export', `users-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Failed to export users. Please try again.');
    } finally {
      exportInProgress.current = false;
      setExporting(false);
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    const formData = new FormData(e.currentTarget);
    const search = formData.get('search') as string;
    setSearchQuery(search);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setCurrentPage(1);
    setAppliedFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setCurrentPage(1);
    setAppliedFilters(defaultFilters);
  };

  const filterFields: TableFilterField[] = [
    { id: 'plans', label: 'Plan', type: 'multi-select', value: appliedFilters.plans,
      options: ALL_PLANS.map(value => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) })),
      placeholder: 'All plans', searchable: false, minSelected: 1,
      active: appliedFilters.plans.length < ALL_PLANS.length,
      onClear: () => { setCurrentPage(1); setAppliedFilters(previous => ({ ...previous, plans: [...ALL_PLANS] })); },
      onChange: plans => { setCurrentPage(1); setAppliedFilters(previous => ({ ...previous, plans })); } },
    ...([
      ['isCreator', 'Creator access', 'Enabled', 'Not enabled'],
      ['isCreatorDisabled', 'Creator restriction', 'Restricted', 'Not restricted'],
      ['isAdmin', 'Admin', 'Admin', 'Non admin'],
      ['isInternal', 'Internal', 'Internal only', 'External only'],
      ['canSetExtensionPublic', 'Public Right', 'Can set public', 'Cannot set public'],
    ] as const).map(([id, label, yes, no]): TableFilterField => ({ id, label, type: 'select', value: appliedFilters[id],
      options: [{ value: 'all', label: 'All' }, { value: 'true', label: yes }, { value: 'false', label: no }],
      active: appliedFilters[id] !== 'all', onClear: () => handleFilterChange(id, 'all'), onChange: value => handleFilterChange(id, value) })),
    ...([
      ['subscribePlatform', 'Subscription platform', [['AppleAppStore', 'Apple App Store'], ['GooglePlayStore', 'Google Play Store'], ['WebStore', 'Web Store']]],
      ['planSource', 'Plan source', [['admin', 'Admin grant'], ['stripe_purchase', 'Stripe purchase'], ['revenuecat_purchase', 'App store purchase'], ['revenuecat_promotional', 'Promotional grant']]],
    ] as const).map(([id, label, options]): TableFilterField => ({ id, label, type: 'select', value: appliedFilters[id],
      options: [{ value: 'all', label: 'All' }, ...options.map(([value, label]) => ({ value, label }))],
      active: appliedFilters[id] !== 'all', onClear: () => handleFilterChange(id, 'all'), onChange: value => handleFilterChange(id, value) })),
  ];

  const refreshCurrentView = () => {
    setSummaryVersion(value => value + 1);
    if (effectiveViewMode === 'grid') fetchGridUsers(1, true);
    else fetchUsers(currentPage, searchQuery, appliedFilters, pageSize);
  };

  const openDetailsModal = (user: User) => {
    setSelectedUser(user);
    setDetailsModalOpen(true);
    // Reflect the opened user in the URL (/users/:userId).
    navigate(`/users/${user.userId}`);
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedUser(null);
    navigate('/users');
  };

  // Drive the modal from the URL: open (fetching the user if we don't already
  // have it, e.g. on a fresh page load / refresh of /users/:userId) or close.
  useEffect(() => {
    if (!routeUserId) {
      // Back on /users — make sure the modal is closed.
      setDetailsModalOpen(false);
      setSelectedUser(null);
      return;
    }
    // Already showing this user (e.g. just opened via a row click) — no refetch.
    if (selectedUser?.userId === routeUserId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest<{ success: boolean; data: User }>(`/api/admin/users/${routeUserId}`);
        if (cancelled) return;
        if (res.success && res.data) {
          setSelectedUser(res.data);
          setDetailsModalOpen(true);
        } else {
          navigate('/users', { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load user', err);
        navigate('/users', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeUserId]);

  if (effectiveViewMode === 'list' && loading && !pagination) {
    return (
      <div className="page-status">Loading users...</div>
    );
  }

  return (
    <div className="page">
      <div className="table-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Users</h1>
          <button type="button" onClick={handleExport} disabled={exporting}
            aria-busy={exporting} title="Download all users with subscription, credits, purchases and activity data"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', cursor: exporting ? 'wait' : 'pointer', opacity: exporting ? 0.65 : 1 }}>
            <FiDownload size={15} /> {exporting ? 'Exporting…' : 'Export all users (CSV)'}
          </button>
        </div>
        <SubscriberSummary refreshVersion={summaryVersion} />
        <div className="table-list-toolbar">
          <form onSubmit={handleSearch} className="table-list-search">
            <input
              type="text"
              name="search"
              placeholder="Search users..."
              defaultValue={searchQuery}
              style={{ flex: 1, minWidth: 0, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            />
            <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
              Search
            </button>
          </form>
          <div className="table-list-actions">
            <TableSort value={sort} onChange={changeSort} loading={effectiveViewMode === 'list' ? loading : gridInitialLoading} options={[
              ['createdAt', 'Joined'], ['updatedAt', 'Updated'], ['name', 'Name'], ['plan', 'Plan'], ['canSetExtensionPublic', 'Public Right'], ['totalCredits', 'Credits'], ['diamonds', 'Diamonds'],
              ['followersCount', 'Followers'], ['followingCount', 'Following'], ['likedExtensionsCount', 'Liked'], ['extensionsCreatedCount', 'Lenses'],
            ].map(([value, label]) => ({ value, label }))} />
          {!isMobile && (
            <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: '4px', overflow: 'hidden', flexShrink: 0 }}>
              {(['list', 'grid'] as ViewMode[]).map((mode, idx) => {
                const active = viewMode === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    title={mode === 'list' ? 'List view' : 'Grid view'}
                    style={{
                      padding: '8px 12px',
                      border: 'none',
                      borderLeft: idx === 0 ? 'none' : '1px solid var(--color-border)',
                      backgroundColor: active ? 'var(--color-accent-strong)' : 'var(--color-surface-hover)',
                      color: active ? 'white' : 'var(--color-muted)',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {mode === 'list' ? <FiList size={15} /> : <FiGrid size={15} />}
                    {mode === 'list' ? 'List' : 'Grid'}
                  </button>
                );
              })}
            </div>
          )}
          <button
            onClick={() => setShowFilters(prev => !prev)}
            style={{
              padding: '8px 16px',
              backgroundColor: showFilters ? 'var(--color-accent-strong)' : 'var(--color-surface-hover)',
              color: showFilters ? 'white' : 'var(--color-text)',
              border: '1px solid var(--color-border)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexShrink: 0,
            }}
          >
            <FiFilter size={14} /> Filter
            {activeFilterCount > 0 && (
              <span style={{
                backgroundColor: 'var(--color-accent-strong)',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
              }}>
                {activeFilterCount}
              </span>
            )}
          </button>
          </div>
        </div>
      </div>


      {showFilters && <TableFilters label="User filters" fields={filterFields} onClearAll={handleClearFilters} />}

      {exportError && (
        <div role="alert" style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: 'var(--color-danger)', borderRadius: '4px', marginBottom: '20px' }}>
          {exportError}
          <button type="button" aria-label="Dismiss export error" onClick={() => setExportError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
        </div>
      )}

      {error && (
        <div style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: 'var(--color-danger)', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {effectiveViewMode === 'list' && pagination && (
        <div style={{ marginBottom: '16px', color: 'var(--color-muted)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>Showing {users.length} of {pagination.total} users</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <label htmlFor="users-page-size">Per page:</label>
            <select
              id="users-page-size"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              style={{ padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: 'var(--color-input)', color: 'var(--color-text)', fontSize: '13px' }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </span>
        </div>
      )}

      {effectiveViewMode === 'list' && (
        <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
          <table aria-busy={loading} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)', width: '48px' }}></th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'name' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="name" label="Name" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'plan' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="plan" label="Plan" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'totalCredits' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="totalCredits" label="Credits" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'followersCount' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="followersCount" label="Followers" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'followingCount' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="followingCount" label="Following" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'likedExtensionsCount' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="likedExtensionsCount" label="Liked" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'extensionsCreatedCount' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="extensionsCreatedCount" label="Lenses" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'diamonds' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="diamonds" label="Diamonds" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'createdAt' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="createdAt" label="Joined" value={sort} onChange={changeSort} /></th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} aria-sort={sort.field === 'canSetExtensionPublic' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><SortHeading field="canSetExtensionPublic" label="Public Right" value={sort} onChange={changeSort} /></th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.userId}
                  onClick={() => openDetailsModal(user)}
                  style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer', transition: 'background-color 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={{ padding: '12px', width: '48px' }}>
                    <Avatar user={user} size={36} />
                  </td>
                  <td style={{ padding: '12px', fontWeight: '500', color: 'var(--color-text)' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      {user.preferredName || user.name || '-'}
                      {user.isAdmin && <AdminBadge />}
                      {user.isInternal && <InternalBadge />}
                    </div>
                    {user.preferred_username && <div style={{ fontSize: '12px', color: 'var(--color-muted)' }}>@{user.preferred_username.replace(/^@+/, '')}</div>}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <PlanBadge user={user} />
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--color-text)' }}>{user.totalCredits}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--color-text)' }}>{user.followersCount ?? 0}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--color-text)' }}>{user.followingCount ?? 0}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--color-text)' }}>{user.likedExtensionsCount ?? 0}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--color-text)' }}>{user.extensionsCreatedCount ?? 0}</td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <DiamondCount count={user.diamonds} />
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px', color: 'var(--color-muted)' }}>{formatDate(user.createdAt)}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <PublicRightStatus user={user} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {effectiveViewMode === 'list' && pagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.pages}
          onPageChange={handlePageChange}
        />
      )}

      {/* ===== GRID VIEW ===== */}
      {effectiveViewMode === 'grid' && (
        <>
          {gridTotal > 0 && !gridInitialLoading && (
            <div style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--color-muted)' }}>
              Showing {gridUsers.length} of {gridTotal.toLocaleString()} users
            </div>
          )}
          {gridInitialLoading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading users…</div>
          ) : gridUsers.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--color-muted)' }}>No users found.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
                {gridUsers.map((user) => (
                  <UserGridCard key={user.userId} user={user} onClick={() => openDetailsModal(user)} />
                ))}
              </div>
              {/* Infinite scroll sentinel + loading indicator */}
              <div ref={sentinelRef} style={{ height: '1px' }} />
              {gridLoadingMore && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)', fontSize: '14px' }}>Loading more…</div>
              )}
              {!gridHasMore && !gridLoadingMore && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-subtle)', fontSize: '13px' }}>End of results</div>
              )}
            </>
          )}
        </>
      )}

      {/* User Details Modal */}
      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          isOpen={detailsModalOpen}
          onClose={closeDetailsModal}
          onStatusChanged={refreshCurrentView}
        />
      )}
    </div>
  );
}
